<!--
  AdminSupportStaffModal.vue

  Modal container for the admin fallback gate (D5/D6 of
  docs/exec-plans/completed/2026-06-23-admin-otp-gate-validateSecurityPermissionNoPrompt.md,
  mirrored from Retail POS into Restaurant POS).

  Orchestrates the two-step flow:
    1. 'user'  — AdminSupportStaffPicker picks a support staff and requests an OTP.
    2. 'otp'   — AdminOtpInput verifies the OTP.
    3. 'done'  — Modal has resolved; parent will unmount us.

  Cancellation paths:
    - Cancel button on either step → emit('cancel') → parent resolves(false).
    - Overlay backdrop click → emit('cancel') → parent resolves(false).
    - ESC keypress → emit('cancel') → parent resolves(false).
    - 3 wrong OTP attempts → child emits('failed') → parent resolves(false).

  Success path:
    - Child OTP step emits('verified', { username }) → parent resolves(true)
      and forwards the username so the caller can recordlogin + set
      localStorage.supportAgent.
-->
<template>
  <div
    class="admin-gate-backdrop"
    @click.self="onBackdropClick"
    role="dialog"
    aria-modal="true"
    aria-labelledby="adminGateTitle"
  >
    <div class="admin-gate-modal" @keydown.esc.stop="onEsc">
      <button
        type="button"
        class="admin-gate-close"
        aria-label="Close"
        @click="$emit('cancel')"
      >
        &times;
      </button>
      <h5 id="adminGateTitle" class="visually-hidden">Admin support staff verification</h5>
      <component
        :is="stepComponent"
        :security="security"
        :selected-user="selectedUser"
        @selected="onSelected"
        @verified="onVerified"
        @failed="onFailed"
        @cancel="$emit('cancel')"
      />
    </div>
  </div>
</template>

<script>
import AdminSupportStaffPicker from './AdminSupportStaffPicker.vue';
import AdminOtpInput from './AdminOtpInput.vue';

export default {
  name: 'AdminSupportStaffModal',
  components: { AdminSupportStaffPicker, AdminOtpInput },
  props: {
    /**
     * The permission key being gated (e.g. 'Loggoff', 'BelowCostSale').
     * Surfaced to the picker step's copy so the operator knows what they're
     * authorising.
     */
    security: { type: String, required: true },
  },
  emits: ['verified', 'cancel'],
  data() {
    return {
      step: 'user', // 'user' | 'otp'
      selectedUser: null,
    };
  },
  computed: {
    stepComponent() {
      return this.step === 'otp' ? AdminOtpInput : AdminSupportStaffPicker;
    },
  },
  mounted() {
    // Focus first interactive element so keyboard users land in the right place.
    this.$nextTick(() => {
      const focusable = this.$el.querySelector('select, input, button');
      if (focusable && typeof focusable.focus === 'function') focusable.focus();
    });
  },
  methods: {
    onSelected(user) {
      this.selectedUser = user;
      this.step = 'otp';
    },
    onVerified(payload) {
      // payload = { username }
      this.$emit('verified', payload);
    },
    onFailed(_msg) {
      // 3 wrong attempts — close the modal; gate resolves false.
      this.$emit('cancel');
    },
    onBackdropClick() {
      this.$emit('cancel');
    },
    onEsc() {
      this.$emit('cancel');
    },
  },
};
</script>

<style lang="scss" scoped>
.admin-gate-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  // Must sit ABOVE the Supervisor Approval Required modal in
  // UserSecurityChecker.vue (z-index 9999) so the gate picker/OTP is the
  // modal the operator actually sees while the supervisor modal stays open
  // underneath. Without this, the gate renders behind the supervisor modal
  // and the operator cannot interact with it.
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.admin-gate-modal {
  position: relative;
  width: 100%;
  max-width: 440px;
  background: var(--card-bg, #fff);
  color: var(--text-primary, #1a1a1a);
  border: var(--card-border, 1px solid rgba(0, 0, 0, 0.08));
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.35);
  padding: 1.75rem 1.5rem;
  max-height: 90vh;
  overflow-y: auto;
}
.admin-gate-close {
  position: absolute;
  top: 0.5rem;
  right: 0.75rem;
  background: transparent;
  border: none;
  font-size: 1.75rem;
  line-height: 1;
  color: var(--text-secondary, #6c757d);
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  &:hover {
    color: var(--accent-electric-blue, #339cff);
  }
}
.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>